(function () {
  "use strict";

  var headlessPlaylist = null;
  var awPlayerReadyPromise = null;

  var state = {
    playlistId: "",
    productId: null,
    trackNumber: 1,
    playing: false,
  };

  function toPositiveInteger(value) {
    var parsed = Number.parseInt(String(value || "").trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function parsePlayHash(hash) {
    var value = String(hash || "").replace(/^#/, "");
    var productTrackMatch = value.match(/^play-(\d+)(?:-(\d+))?$/);

    if (productTrackMatch) {
      return {
        productId: toPositiveInteger(productTrackMatch[1]),
        trackNumber: toPositiveInteger(productTrackMatch[2]) || 1,
      };
    }

    if (value.indexOf("play?") === 0) {
      var playParams = new URLSearchParams(value.slice(5));

      return {
        productId: toPositiveInteger(playParams.get("product")),
        playlistId: playParams.get("playlist") || "",
        trackNumber: toPositiveInteger(playParams.get("track")) || 1,
      };
    }

    var hashParamString = value;
    var firstAmpersand = hashParamString.indexOf("&");
    var firstQuestionMark = hashParamString.indexOf("?");

    if (firstQuestionMark >= 0) {
      hashParamString = hashParamString.slice(firstQuestionMark + 1);
    } else if (
      firstAmpersand >= 0 &&
      hashParamString.slice(0, firstAmpersand).indexOf("=") === -1
    ) {
      hashParamString = hashParamString.slice(firstAmpersand + 1);
    }

    var hashParams = new URLSearchParams(hashParamString);
    var awplayTrack = toPositiveInteger(hashParams.get("awplay"));

    if (awplayTrack) {
      return {
        productId: toPositiveInteger(hashParams.get("product")),
        playlistId: hashParams.get("playlist") || "",
        trackNumber: awplayTrack,
      };
    }

    return null;
  }

  function parsePlayUrl(urlString) {
    try {
      var url = new URL(urlString, window.location.href);
      var target = parsePlayHash(url.hash);

      if (target) return target;

      var awplayTrack = toPositiveInteger(url.searchParams.get("awplay"));
      if (!awplayTrack) return null;

      return {
        productId: toPositiveInteger(url.searchParams.get("product")),
        playlistId: url.searchParams.get("playlist") || "",
        trackNumber: awplayTrack,
      };
    } catch (error) {
      return null;
    }
  }

  function getButtonTarget(button) {
    var target = parsePlayUrl(button.getAttribute("href"));

    if (!target) {
      target = {};
    }

    target.productId =
      target.productId || toPositiveInteger(button.dataset.awplayerProductId);
    target.trackNumber =
      target.trackNumber ||
      toPositiveInteger(button.dataset.awplayerTrackNumber) ||
      1;

    return target.productId || target.playlistId ? target : null;
  }

  function sameTarget(target) {
    if (!target) return false;

    if (state.playlistId && target.playlistId) {
      return (
        state.playlistId === target.playlistId &&
        state.trackNumber === (target.trackNumber || 1)
      );
    }

    return (
      state.productId === target.productId &&
      state.trackNumber === (target.trackNumber || 1)
    );
  }

  function getTriggerButtons() {
    return Array.from(
      document.querySelectorAll("[data-awplayer-trigger][href]"),
    );
  }

  function renderButtons() {
    getTriggerButtons().forEach(function (button) {
      var target = getButtonTarget(button);
      var isPlaying = Boolean(state.playing && sameTarget(target));
      var playIcon = button.querySelector(
        ".reply-media-action-btn__icon--play",
      );
      var pauseIcon = button.querySelector(
        ".reply-media-action-btn__icon--pause",
      );
      var playLabel = button.dataset.awplayerPlayLabel || "Play audio";
      var pauseLabel = button.dataset.awplayerPauseLabel || "Pause audio";

      button.classList.toggle("is-playing", isPlaying);
      button.setAttribute("aria-pressed", String(isPlaying));
      button.setAttribute("aria-label", isPlaying ? pauseLabel : playLabel);

      if (playIcon) playIcon.hidden = isPlaying;
      if (pauseIcon) pauseIcon.hidden = !isPlaying;
    });
  }

  function setPlaybackState(target, playing) {
    state.playlistId = target && target.playlistId ? target.playlistId : "";
    state.productId = target && target.productId ? target.productId : null;
    state.trackNumber = target && target.trackNumber ? target.trackNumber : 1;
    state.playing = Boolean(playing && (state.productId || state.playlistId));
    renderButtons();
  }

  function isAwplayerDomPlaying() {
    return Boolean(
      document.querySelector(".awp1v-player-embed.awp1v-player--playing"),
    );
  }

  function syncPlaybackStateFromDom() {
    if (!state.productId && !state.playlistId) return;

    setPlaybackState(state, isAwplayerDomPlaying());
  }

  function shouldSyncPlaybackState(mutations) {
    return mutations.some(function (mutation) {
      if (
        mutation.target instanceof Element &&
        mutation.target.closest(".awp1v-player-embed")
      ) {
        return true;
      }

      return Array.from(mutation.addedNodes).some(function (node) {
        return (
          node instanceof Element &&
          (node.matches(".awp1v-player-embed") ||
            Boolean(node.querySelector(".awp1v-player-embed")))
        );
      });
    });
  }

  function hasAddedTriggerButton(mutations) {
    return mutations.some(function (mutation) {
      return Array.from(mutation.addedNodes).some(function (node) {
        return (
          node instanceof Element &&
          (node.matches("[data-awplayer-trigger][href]") ||
            Boolean(node.querySelector("[data-awplayer-trigger][href]")))
        );
      });
    });
  }

  function whenAWPlayerReady() {
    if (awPlayerReadyPromise) return awPlayerReadyPromise;

    awPlayerReadyPromise = new Promise(function (resolve, reject) {
      var attempts = 0;
      var maxAttempts = 100;

      function attachReadyListener() {
        if (!window.AWPlayer || typeof window.AWPlayer.on !== "function") {
          attempts += 1;

          if (attempts >= maxAttempts) {
            reject(new Error("AWPlayer API was not found."));
            return;
          }

          window.setTimeout(attachReadyListener, 100);
          return;
        }

        window.AWPlayer.on("ready", function () {
          resolve(window.AWPlayer);
        });
      }

      attachReadyListener();
    });

    return awPlayerReadyPromise;
  }

  function findPlaylistById(playlists, playlistId) {
    if (!playlistId) return null;

    return playlists.find(function (playlist) {
      return playlist.playlistId === playlistId;
    });
  }

  function findPlaylistByProductId(playlists, productId) {
    if (!productId) return null;

    return playlists.find(function (playlist) {
      return String(playlist.productId) === String(productId);
    });
  }

  function getAllPlaylists() {
    if (typeof window.AWPlayer.getPlaylists !== "function") return [];

    return window.AWPlayer.getPlaylists() || [];
  }

  function getProductPlaylists(productId) {
    if (!productId || typeof window.AWPlayer.getPlaylists !== "function") {
      return [];
    }

    return window.AWPlayer.getPlaylists({ productId: productId }) || [];
  }

  function destroyHeadlessPlaylist() {
    if (!headlessPlaylist || typeof headlessPlaylist.destroy !== "function") {
      return;
    }

    headlessPlaylist.destroy();
    headlessPlaylist = null;
  }

  function createHeadlessPlaylist(productId) {
    if (!productId || typeof window.AWPlayer.createPlaylist !== "function") {
      return Promise.resolve(null);
    }

    destroyHeadlessPlaylist();

    return window.AWPlayer
      .createPlaylist({ productId: productId })
      .then(function (playlist) {
        headlessPlaylist = playlist;
        return playlist;
      });
  }

  function resolvePlaylist(target) {
    var playlists = getProductPlaylists(target.productId);
    var playlist = findPlaylistById(playlists, target.playlistId);

    if (playlist) return Promise.resolve(playlist);

    playlist = findPlaylistByProductId(playlists, target.productId);
    if (playlist) return Promise.resolve(playlist);

    playlists = getAllPlaylists();
    playlist =
      findPlaylistById(playlists, target.playlistId) ||
      findPlaylistByProductId(playlists, target.productId);

    if (playlist) return Promise.resolve(playlist);

    if (target.productId) return createHeadlessPlaylist(target.productId);

    return Promise.resolve(playlists.length === 1 ? playlists[0] : null);
  }

  function clickActivePlayerPauseButton() {
    var pauseButton = document.querySelector(
      ".awp1v-player-embed.awp1v-player--playing button.awp1v-player--pause",
    );

    if (pauseButton instanceof HTMLButtonElement) {
      pauseButton.click();
      return true;
    }

    return false;
  }

  function pauseCurrentPlayback() {
    if (window.AWPlayer && typeof window.AWPlayer.pause === "function") {
      window.AWPlayer.pause();
    } else if (headlessPlaylist && typeof headlessPlaylist.pause === "function") {
      headlessPlaylist.pause();
    } else {
      clickActivePlayerPauseButton();
    }

    setPlaybackState(state, false);
  }

  function bindAWPlayerEvents() {
    whenAWPlayerReady()
      .then(function () {
        window.AWPlayer.on("play", function (data) {
          var playlistId = data.playlistId || "";

          setPlaybackState(
            {
              playlistId: playlistId,
              productId:
                playlistId && playlistId === state.playlistId
                  ? state.productId
                  : null,
              trackNumber: (data.trackIndex || 0) + 1,
            },
            true,
          );
        });

        window.AWPlayer.on("pause", function () {
          setPlaybackState(state, false);
        });

        window.AWPlayer.on("ended", function () {
          setPlaybackState(state, false);
        });
      })
      .catch(function () {});
  }

  function playTarget(target) {
    return whenAWPlayerReady()
      .then(function () {
        return resolvePlaylist(target);
      })
      .then(function (playlist) {
        var trackIndex = (target.trackNumber || 1) - 1;

        if (!playlist || !playlist.playlistId) {
          throw new Error("No matching AWPlayer playlist was found.");
        }

        if (playlist.tracks && trackIndex >= playlist.tracks.length) {
          throw new Error(
            "AWPlayer track " +
              target.trackNumber +
              " is outside playlist " +
              playlist.playlistId +
              ".",
          );
        }

        setPlaybackState(
          {
            playlistId: playlist.playlistId,
            productId: target.productId || playlist.productId,
            trackNumber: target.trackNumber || 1,
          },
          true,
        );

        window.AWPlayer.play({
          playlistId: playlist.playlistId,
          trackIndex: trackIndex,
        });
      })
      .catch(function (error) {
        console.warn("AWPlayer deep link:", error.message);
        setPlaybackState(target, false);
      });
  }

  function handlePlayLink(event) {
    var targetElement = event.target instanceof Element ? event.target : null;
    var link = targetElement
      ? targetElement.closest('a[href*="#play"], a[href*="awplay"]')
      : null;

    if (!link) return;

    var target = link.matches("[data-awplayer-trigger]")
      ? getButtonTarget(link)
      : parsePlayUrl(link.getAttribute("href"));

    if (!target) return;

    event.preventDefault();
    history.pushState({}, "", link.getAttribute("href"));

    if (state.playing && sameTarget(target)) {
      pauseCurrentPlayback();
      return;
    }

    playTarget(target);
  }

  function handleInitialUrl() {
    var target = parsePlayUrl(window.location.href);
    if (!target) return;

    playTarget(target);
  }

  function styleAwplayLinks(root) {
    root.querySelectorAll('a[href*="awplay"]').forEach(function (link) {
      if (parsePlayUrl(link.getAttribute("href"))) {
        link.classList.add("reply-awplay-link");
      }
    });
  }

  function observeDynamicAwplayerMarkup() {
    if (typeof MutationObserver !== "function") return;

    var observer = new MutationObserver(function (mutations) {
      if (hasAddedTriggerButton(mutations)) {
        renderButtons();
      }

      if (shouldSyncPlaybackState(mutations)) {
        syncPlaybackStateFromDom();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["class"],
    });
  }

  document.addEventListener("click", handlePlayLink);
  document.addEventListener("shopify:section:load", function (event) {
    styleAwplayLinks(event.target);
    renderButtons();
  });
  window.addEventListener("hashchange", handleInitialUrl);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      styleAwplayLinks(document);
      renderButtons();
      handleInitialUrl();
    });
  } else {
    styleAwplayLinks(document);
    renderButtons();
    handleInitialUrl();
  }

  bindAWPlayerEvents();
  observeDynamicAwplayerMarkup();
})();
